import { useMemo } from 'react';
import { Users, CreditCard, Pill } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { KpiTile } from '@/components/ui/KpiTile';
import { Badge } from '@/components/ui/Badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import { LoadingState, EmptyState, ErrorState } from '@/components/ui/States';
import { ApiError } from '@/lib/api/client';
import { usePatients } from '../patients/usePatients';
import { useInventory } from '../pharmacy/useInventory';
import { useDailySummary } from './useDailySummary';
import PharmacyReportsPanel from './PharmacyReportsPanel';
import DayBookPanel from './DayBookPanel';
import PharmacySubstitutionPanel from './PharmacySubstitutionPanel';

/**
 * Sixth screen migrated off Create React App — see HMS_V4_API_CONTRACT.md
 * and QueueView.tsx for the established pattern: a drop-in replacement for
 * src/components/ReportsModule.jsx (itself already extracted out of
 * BillingModule.jsx during the Billing migration — see that file's header),
 * swapped into App.jsx by changing one import line.
 *
 * By far the smallest screen migrated this session: three data sources, two
 * of which (usePatients, useInventory) already existed as shared hooks from
 * the Appointments and Pharmacy migrations — only useDailySummary is new.
 *
 * Reproduces the legacy all-or-nothing loading gate (nothing renders until
 * patients, the daily summary, and inventory have all loaded, same as the
 * original Promise.all) rather than letting each card load progressively —
 * a genuine UX improvement would be reasonable here, but is a behaviour
 * change beyond what a faithful migration calls for in this pass.
 */
const isExpired = (d: string | null) => !!d && new Date(d) < new Date();
// is_low comes from the server: the store's reorder level when set, else the batch threshold.
const isLow = (i: { quantity_available: number; low_stock_threshold: number; is_low?: boolean }) => i.is_low ?? i.quantity_available <= i.low_stock_threshold;

export default function ReportsModule() {
  const patientsQuery = usePatients();
  const summaryQuery = useDailySummary();
  const inventoryQuery = useInventory();

  const loading = patientsQuery.isLoading || summaryQuery.isLoading || inventoryQuery.isLoading;
  const firstError = [patientsQuery.error, summaryQuery.error, inventoryQuery.error].find((e) => e instanceof ApiError) as
    | ApiError
    | undefined;

  const patients = patientsQuery.data ?? [];
  const summary = summaryQuery.data;
  const inventory = inventoryQuery.data ?? [];

  // Deliberately filters the full inventory client-side rather than using
  // summary.lowStockAlerts, which the backend already computes with the same
  // logic — that list omits batch_number, and pharmacy staff need the batch
  // to act on a specific physical unit of stock, not just a medicine name.
  // See types.ts for the fuller reasoning.
  const alerts = useMemo(() => inventory.filter((i) => isLow(i) || isExpired(i.expiry_date)), [inventory]);

  if (firstError) {
    return <ErrorState className="m-5" message={`Failed to load reports: ${firstError.message}`} />;
  }
  if (loading || !summary) {
    return <LoadingState className="m-5" label="Loading reports…" />;
  }

  return (
    <div className="fade-in">
      <div className="grid3 mb-5">
        <KpiTile
          icon={<Users className="h-4 w-4" />} iconClassName="bg-info-soft text-info-ink" accentClassName="border-l-[3px] border-l-teal"
          value={patients.length} label="Total patients"
        />
        <KpiTile
          icon={<CreditCard className="h-4 w-4" />} iconClassName="bg-info-soft text-info-ink" accentClassName="border-l-[3px] border-l-info"
          value={`₹${summary.opd.revenue.toFixed(0)}`} label="OPD revenue" sub={`${summary.opd.count} bills`}
        />
        <KpiTile
          icon={<Pill className="h-4 w-4" />} iconClassName="bg-success-soft text-success-ink" accentClassName="border-l-[3px] border-l-success"
          value={`₹${summary.pharmacy.revenue.toFixed(0)}`} label="Pharmacy revenue" sub={`${summary.pharmacy.count} bills`}
        />
      </div>

      <div className="grid2">
        <Card>
          <div className="mb-3.5 text-sm font-semibold">Patient register</div>
          {patients.length === 0 ? (
            <EmptyState label="No patients yet" />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>{['UHID', 'Name', 'Age / Gender', 'Registered'].map((h) => <TableHead key={h}>{h}</TableHead>)}</TableRow>
              </TableHeader>
              <TableBody>
                {patients.map((p) => (
                  <TableRow key={p.uhid}>
                    <TableCell className="font-mono text-[11px] font-semibold text-teal">{p.uhid}</TableCell>
                    <TableCell className="font-semibold">{p.name}</TableCell>
                    <TableCell className="text-ink-muted">{p.age}y, {p.gender}</TableCell>
                    <TableCell className="text-xs text-ink-muted">{new Date(p.created_at).toLocaleDateString('en-IN')}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>

        <Card>
          <div className="mb-3.5 text-sm font-semibold">Low stock &amp; expiry alerts</div>
          {alerts.length === 0 ? (
            <EmptyState label="All stock OK ✓" />
          ) : (
            alerts.map((med) => (
              <div key={med.id} className="flex justify-between border-b border-[#F3F4F6] py-2.5 last:border-b-0">
                <div>
                  <div className="text-[13px] font-semibold">{med.generic_name} {med.strength}</div>
                  <div className="text-[11px] text-ink-muted">
                    Batch: {med.batch_number} · Exp: {med.expiry_date ? new Date(med.expiry_date).toLocaleDateString('en-IN') : '—'}
                  </div>
                </div>
                <Badge variant={isExpired(med.expiry_date) ? 'danger' : 'warn'}>
                  {isExpired(med.expiry_date) ? 'Expired' : `${med.quantity_available} left`}
                </Badge>
              </div>
            ))
          )}
        </Card>
      </div>

      {/* Loads on its own date range rather than joining the all-or-nothing
          gate above — a pharmacist reconciling last week should not be made
          to wait on today's patient roster, and vice versa. */}
      <DayBookPanel />
      <PharmacyReportsPanel />
      <PharmacySubstitutionPanel />
    </div>
  );
}
